import { Divider, Form, FormInstance } from 'antd';
import { useMemo } from 'react';
import { StyledApplicationSummary } from '../style';
import {
  Listing,
  ListingApplication,
  ApplicationProductData,
  ApplicationDeliveryCondition,
  ApplicationCertificate,
  FarmProduct,
} from '../../../../../types/ListingData';
import { ProducerComments } from './ProducerComments';
import { Certificates } from './Certificates';
import { Products } from './Products';
import { Deliveries } from './Deliveries';
import { QualityMarkOption } from 'utils/qualityMarkHelpers';

interface ApplicationSummaryProps {
  applicationData?: ListingApplication | null;
  listingData?: Listing | null;
  certificates?: ApplicationCertificate[];
  form?: FormInstance;
  farmProductNames?: { [key: number]: string };
  availableProductsByCategory?: { [categoryId: number]: FarmProduct[] };
  onCertificateDelete?: (certificate: ApplicationCertificate) => void;
  allQualityMarks?: QualityMarkOption[];
}

export const ApplicationSummary = ({
  applicationData,
  listingData,
  certificates = [],
  form,
  farmProductNames = {},
  availableProductsByCategory = {},
  onCertificateDelete,
  allQualityMarks,
}: ApplicationSummaryProps) => {
  const producerComment = Form.useWatch('producer_comments', form);
  const warehouseAddress = Form.useWatch('warehouse_address', form);
  const formProducts =
    (Form.useWatch('application_products', form) as
      | ApplicationProductData[]
      | undefined) || [];
  const deliveryConditions =
    (Form.useWatch('delivery_conditions', form) as
      | ApplicationDeliveryCondition[]
      | undefined) || [];

  const displayProducts = useMemo(() => {
    if (!listingData?.products) {
      return [];
    }

    const result = listingData.products
      .filter((listingProduct) => listingProduct.id !== undefined)
      .map((listingProduct) => {
        const formProduct = formProducts.find(
          (fp: ApplicationProductData) =>
            fp.listing_product_id === listingProduct.id
        );

        const merged = {
          listing_product_id: listingProduct.id!,
          offer_type: formProduct?.offer_type || 'full_amount',
          farm_product_id: formProduct?.farm_product_id || null,
          offered_quantity: formProduct?.offered_quantity,
          offered_unit_id:
            formProduct?.offered_unit_id || listingProduct.storage_unit?.id,
          offered_price: formProduct?.offered_price,
          offered_price_unit_id:
            formProduct?.offered_price_unit_id ||
            listingProduct.storage_unit?.id,
          listingProduct,
        };

        return merged;
      });

    return result;
  }, [listingData?.products, formProducts]);

  const displayDeliveries = useMemo(() => {
    if (!listingData?.delivery_conditions) {
      return deliveryConditions;
    }

    const hasFreeFormDelivery = listingData.delivery_conditions.some(
      (condition) => condition.delivery_type === 'free_form'
    );

    if (hasFreeFormDelivery) {
      return deliveryConditions;
    }

    if (deliveryConditions && deliveryConditions.length > 0) {
      return deliveryConditions;
    } else {
      return listingData.delivery_conditions.filter(
        (condition) =>
          condition.delivery_type === 'delivery_conditions' ||
          condition.delivery_type === 'detailed_delivery'
      );
    }
  }, [listingData?.delivery_conditions, deliveryConditions]);

  return (
    <StyledApplicationSummary>
      <ProducerComments
        producerComment={producerComment}
        applicationData={applicationData}
      />

      <Divider />

      {certificates.length > 0 && (
        <>
          <Certificates
            certificates={certificates}
            onDelete={onCertificateDelete}
          />
          <Divider />
        </>
      )}

      <Products
        displayProducts={displayProducts}
        farmProductNames={farmProductNames}
        availableProductsByCategory={availableProductsByCategory}
        allQualityMarks={allQualityMarks}
      />

      <Divider />

      <Deliveries
        displayDeliveries={displayDeliveries}
        listingData={listingData}
        warehouseAddress={warehouseAddress}
        farmProductNames={farmProductNames}
        applicationProducts={formProducts}
      />
    </StyledApplicationSummary>
  );
};
